import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import axiosInstance from '../lib/axiosInstance';
import { AppErrorResponse, PageableResponse } from '../types';
import {
  ProductionOrder,
  ProductionOrderListParams,
  ProductionOrderWorkflowStep,
} from '../types/ProductionOrder';

const BASE = '/production/orders';

// Structured, hierarchical query keys so we can invalidate precisely
// (instead of the app-wide refetchQueries() pattern flagged in the audit).
export const productionOrderKeys = {
  all: ['productionOrders'] as const,
  lists: () => [...productionOrderKeys.all, 'list'] as const,
  list: (params: ProductionOrderListParams) =>
    [...productionOrderKeys.lists(), params] as const,
  detail: (id: string | undefined) =>
    [...productionOrderKeys.all, 'detail', id] as const,
  workflows: () => [...productionOrderKeys.all, 'workflows'] as const,
};

export function useProductionOrders(params: ProductionOrderListParams) {
  return useQuery({
    queryKey: productionOrderKeys.list(params),
    queryFn: async () => {
      const { data } = await axiosInstance.get<PageableResponse<ProductionOrder>>(
        BASE,
        { params },
      );
      return data;
    },
  });
}

export function useProductionOrder(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: productionOrderKeys.detail(id),
    queryFn: async () => {
      const { data } = await axiosInstance.get<ProductionOrder>(`${BASE}/${id}`, {
        params: { resource: 'full' },
      });
      return data;
    },
  });
}

type ListCache = PageableResponse<ProductionOrder>;
interface UpdateContext {
  previous: [readonly unknown[], ListCache | undefined][];
}

export function useUpdateProductionOrder() {
  const queryClient = useQueryClient();
  return useMutation<
    ProductionOrder,
    AxiosError<AppErrorResponse>,
    ProductionOrder,
    UpdateContext
  >({
    mutationFn: async (payload) => {
      const { data } = await axiosInstance.put<ProductionOrder>(
        `${BASE}/${payload.id}`,
        payload,
      );
      return data;
    },
    // optimistic update so drag-to-reschedule feels instant
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: productionOrderKeys.lists() });
      const previous = queryClient.getQueriesData<ListCache>({
        queryKey: productionOrderKeys.lists(),
      });
      previous.forEach(([key, data]) => {
        if (!data) return;
        queryClient.setQueryData<ListCache>(key, {
          ...data,
          data: data.data.map((o) => (o.id === payload.id ? { ...o, ...payload } : o)),
        });
      });
      return { previous };
    },
    onError: (_e, _payload, ctx) => {
      ctx?.previous.forEach(([key, data]) =>
        queryClient.setQueryData<ListCache>(key, data),
      );
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: productionOrderKeys.all }),
  });
}

export function useProductionOrderWorkflows() {
  return useQuery({
    queryKey: productionOrderKeys.workflows(),
    staleTime: 60 * 60 * 1000, // workflow rarely changes
    queryFn: async () => {
      // Backend returns [{ seq, status, description }]
      const { data } = await axiosInstance.get<
        { seq: number; status: string; description: string }[]
      >(`${BASE}/workflows`);
      return (data ?? []).map<ProductionOrderWorkflowStep>((w) => ({
        step: w.seq,
        status: w.status as ProductionOrderWorkflowStep['status'],
        description: w.description,
      }));
    },
  });
}
